import { useFormContext } from 'react-hook-form'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import {
  ProjectDocumentRuleType,
  ProjectGridFieldRuleType,
} from '@/types/rules'
import { useDemoModeContext } from '@/components/demo-mode-provider/DemoModeProvider'
import { FormCheckbox, FormSwitch } from '@/components/forms'
import PropertyField from '@/components/project-settings/params-schema-property-fields/PropertyField'
import DependentFieldsAutocomplete from '@/components/rules/DependentFieldsAutocomplete'
import MultiSelectWorkflowStatesAndList from './MultiWorkflowStateSelectAndList'

type CommonRuleConfigurationProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propertiesToRender: [string, any][]
  ruleType: ProjectGridFieldRuleType | ProjectDocumentRuleType
  fieldId?: string
}

export default function CommonRuleConfiguration({
  propertiesToRender,
  ruleType,
  fieldId,
}: CommonRuleConfigurationProps) {
  const isSuperUser = useIsSuperUser()
  const [demoMode] = useDemoModeContext()
  const { watch } = useFormContext()

  // Document rules don't support "run_in_all_workflow_states" at the moment
  const supportsRunInAllWorkflowStates = !!fieldId
  const runInAllWorkflowStates = !!watch('run_in_all_workflow_states')

  return (
    <>
      {ruleType.uses_dependent_fields && (
        <DependentFieldsAutocomplete ruleType={ruleType} fieldId={fieldId} />
      )}
      {propertiesToRender.map(([propertyId, property]) => {
        const required = ruleType.params_schema.required?.includes(propertyId)
        return (
          <PropertyField
            key={`${ruleType.id}-${propertyId}`}
            propertyId={propertyId}
            property={property}
            required={required}
            shrinkLabel
          />
        )
      })}
      {isSuperUser &&
        !demoMode &&
        (ruleType.code === 'run_py' || ruleType.code === 'doc_run_py') && (
          // Wrapping in div to "skip" default MUI margins
          <div>
            <FormCheckbox
              name="is_superuser_rule"
              label="Super User Only"
              sx={{
                color: SUPER_USER_ONLY_COLOR,
                '&.Mui-checked': {
                  color: SUPER_USER_ONLY_COLOR,
                },
              }}
            />
          </div>
        )}
      {supportsRunInAllWorkflowStates && (
        <FormSwitch
          name="run_in_all_workflow_states"
          label="Run in All Workflow States"
        />
      )}
      {(!supportsRunInAllWorkflowStates || !runInAllWorkflowStates) && (
        <MultiSelectWorkflowStatesAndList
          formFieldId="workflow_states_ids"
          label="Run in Workflow States"
          emptyListText="This rule will not run unless it is set to run in at least one workflow state."
        />
      )}
    </>
  )
}
